import { useEffect, useState } from "react";
import api from "@/admin/api";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { Trash2, AlertTriangle, Archive } from "lucide-react";

const DEP_LABELS = {
  waves: "Gelombang", invitations: "Undangan", participants: "Partisipan",
  contact_records: "Kontak", response_sessions: "Sesi respons", real_responses: "Respons nyata",
  answers: "Jawaban", score_snapshots: "Skor", export_events: "Ekspor",
  instruments: "Instrumen", instrument_versions: "Versi instrumen", sections: "Bagian",
  items: "Item", options: "Opsi", scales: "Skala", scoring_model_versions: "Model skor",
  consent_versions: "Consent",
  active_sessions: "Sesi aktif (dicabut)", audit_events_retained: "Audit events (dipertahankan)",
  login_attempts_retained: "Riwayat login (dipertahankan)",
};

const KIND_LABELS = { study: "studi", wave: "gelombang", instrument: "instrumen", user: "pengguna", organization: "organisasi" };

export function DeleteDialog({ kind, id, onClose, onDeleted }) {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [typed, setTyped] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/delete/${kind}/${id}/dry-run`)
      .then((r) => setPlan(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Tidak dapat dihapus"));
  }, [kind, id]);

  // Organisasi dengan undangan/respons: hard-delete diblokir, tawarkan arsip.
  const archiving = Boolean(plan && plan.deletable === false && plan.archivable);
  const blocked = Boolean(plan && plan.deletable === false && !plan.archivable);

  const confirm = async () => {
    setBusy(true);
    try {
      const url = archiving ? `/admin/delete/${kind}/${id}/archive` : `/admin/delete/${kind}/${id}/confirm`;
      const r = await api.post(url, { confirmation: typed, password: password || undefined });
      onDeleted?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || (archiving ? "Pengarsipan gagal" : "Penghapusan gagal"));
    } finally {
      setBusy(false);
    }
  };

  const token = plan?.confirmation_required;
  const deps = plan ? Object.entries(plan.dependencies).filter(([, v]) => v > 0) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="delete-dialog">
      <div className="w-full max-w-md rounded-comiq-lg border bg-comiq-surface-alt p-6" style={{ borderColor: COLORS.line }}>
        <div className="flex items-center gap-2" style={{ color: archiving ? "#B45309" : "#B91C1C" }}>
          <AlertTriangle size={18} />
          <h2 className="text-lg font-semibold">{archiving ? "Arsipkan" : "Hapus"} {KIND_LABELS[kind] || kind}</h2>
        </div>

        {error && <p className="mt-4 rounded-comiq-sm px-3 py-2 text-sm" style={{ background: "#FEE4E2", color: "#B42318" }} data-testid="delete-blocked">{error}</p>}

        {plan && (
          <>
            <p className="mt-3 text-sm" style={{ color: COLORS.textSoft }} data-testid="delete-intro">
              {archiving
                ? "Organisasi ini memiliki data terkait sehingga tidak dapat dihapus permanen. Arsipkan agar histori response, scoring, dan audit tetap utuh:"
                : "Data berikut akan dihapus permanen (backup dibuat otomatis sebelum hapus):"}
            </p>
            <div className="mt-3 rounded-comiq-sm border p-3 text-sm" style={{ borderColor: COLORS.line }} data-testid="delete-deps">
              {deps.length === 0 && <span style={{ color: COLORS.textSoft }}>Tidak ada dependensi.</span>}
              {deps.map(([k, v]) => (
                <div key={k} className="flex justify-between py-0.5" data-testid={`delete-dep-${k}`}>
                  <span style={{ color: COLORS.textSoft }}>{DEP_LABELS[k] || k}</span>
                  <span className="font-mono font-semibold" style={{ color: k === "real_responses" && v > 0 ? "#B91C1C" : COLORS.ink }}>{v}</span>
                </div>
              ))}
            </div>
            {kind === "organization" && (
              <p className="mt-3 rounded-comiq-sm px-3 py-2 text-xs" style={{ background: "#F1FAF3", color: "#15803D" }} data-testid="org-delete-note">
                Menghapus atau mengarsipkan organisasi TIDAK menghapus Study, Gelombang, Instrumen, response, scoring, atau audit.
              </p>
            )}
            {!blocked && (
              <>
                <label className="mt-4 block text-xs font-semibold" style={{ color: COLORS.ink }}>
                  Ketik <span className="font-mono" style={{ color: "#B91C1C" }}>{token}</span> untuk konfirmasi
                </label>
                <input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  className="mt-1 w-full rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none font-mono"
                  style={{ borderColor: COLORS.line, color: COLORS.ink }}
                  data-testid="delete-confirm-input"
                />
              </>
            )}
            {blocked && (
              <p className="mt-4 rounded-comiq-sm px-3 py-2 text-sm" style={{ background: "#FEF3C7", color: "#B45309" }} data-testid="delete-archived-note">
                Organisasi ini sudah diarsipkan.
              </p>
            )}
            {plan.requires_password && !archiving && (
              <>
                <label className="mt-4 block text-xs font-semibold" style={{ color: COLORS.ink }}>
                  Masukkan password Anda untuk re-autentikasi
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none"
                  style={{ borderColor: COLORS.line, color: COLORS.ink }}
                  data-testid="delete-password-input"
                />
              </>
            )}
          </>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-comiq btn-line" data-testid="delete-cancel-btn">Batal</button>
          {plan && !blocked && (
            <button
              onClick={confirm}
              disabled={busy || typed !== token || (plan?.requires_password && !archiving && !password)}
              className="btn-comiq btn-accent disabled:opacity-40"
              style={{ background: archiving ? "#B45309" : "#B91C1C" }}
              data-testid={archiving ? "archive-confirm-btn" : "delete-confirm-btn"}
            >
              {archiving ? <Archive size={14} /> : <Trash2 size={14} />}
              {busy ? "Memproses…" : archiving ? "Arsipkan organisasi" : "Hapus permanen"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
